"use client";
import { IBaseLabelValue, IInquiryDetailsResultValues } from "@/interfaces";
import { CustomButton, FlexBetween, FlexCenter, FlexCol } from "@/components";
import { cn } from "@/lib";
import { useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { routes } from "@/routes";
import DownloadPdf from "../../pdfTemplates/downloadPdf";
import ResultContent from "../../resultContent";
import PdfTemplate from "@/components/pdfTemplates/pdfTemplate";
import React, { useRef } from "react";
import { IRawPurchasedIdentityInquiryResData } from "@/interfaces/inquiries/governmental";

const formatBirthDate = (value: string) => {
  if (!value || value.length !== 8) return value;
  return value.replace(/(\d{4})(\d{2})(\d{2})/, "$1/$2/$3");
};

const convertIdentificationNumber = (input: string) => {
  const match = input.match(/^(\D)(\d{2})$/);

  if (!match) return input;

  let [_, letter, digits] = match;

  if (letter === "ا") {
    letter = "الف";
  }

  return `${digits} - ${letter}`;
};

const IdentityInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedIdentityInquiryResData;
}) => {
  const t = useTranslations("serviceResponseDetails");
  const router = useRouter();
  const response = data.data.data;
  const input = data.data.input;
  const responseEnvelope = data.data as unknown as { message?: unknown };
  const responseMessage =
    typeof responseEnvelope?.message === "string"
      ? responseEnvelope.message
      : null;
  const inputContent: IBaseLabelValue[] = [
    {
      label: t("identityInquiry.nationalCode"),
      value: input?.national_code || "-",
    },
    {
      label: t("identityInquiry.birthDate"),
      value: input?.birth_day ? formatBirthDate(input.birth_day) : "-",
    },
  ];

  //DO NOT SIMPLIFY THIS CONDITION TO !response.matched
  if (!response || response.matched === false || responseMessage) {
    return (
      <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
        <FlexCol className="items-stretch rounded-2xl bg-white p-8">
          <h2 className="mb-5 text-center font-bold lg:text-lg">
            {t("identityInquiry.title")}
          </h2>
          <InputDataContent
            content={inputContent}
            className="le p-6 md:p-8"
            itemClassName="flex-col md:flex-row leading-9 md:leading-none"
          />
          <h2 className="my-5 text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <div className="shadow-main mb-10 rounded-xl p-6 text-center text-sm leading-7 md:p-8 md:text-base">
            <p>
              <span className="text-error-main font-semibold">
                {responseMessage || "کد ملی و تاریخ تولد باهم تطابق ندارند."}
              </span>
            </p>
          </div>
          <FlexCenter className="flex flex-col justify-center gap-4 md:flex-row md:gap-6">
            <CustomButton
              id="details-identity-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-identity-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Identity_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </FlexCenter>
        </FlexCol>
      </div>
    );
  }
  const resultContent: IInquiryDetailsResultValues[] = [
    {
      label: t("identityInquiry.name"),
      value: response.firstName ? response.firstName : "نامشخص",
    },
    {
      label: t("identityInquiry.lastName"),
      value: response.lastName ? response.lastName : "نامشخص",
    },
    {
      label: t("identityInquiry.fatherName"),
      value: response.fatherName ? response.fatherName : "نامشخص",
    },
    {
      label: t("identityInquiry.aliveStatus"),
      value: response.alive !== undefined && response.alive !== null ? (
        response.alive ? (
          <span className="text-success-main font-bold">
            {t("identityInquiry.alive")}
          </span>
        ) : (
          <span className="text-error-main font-bold">
            {t("identityInquiry.dead")}
          </span>
        )
      ) : (
        "نامشخص"
      ),
    },
    {
      label: t("identityInquiry.deathDate"),
      value:
        response.deathDate && response.deathDate !== "0"
          ? response.deathDate
          : "-",
    },
    {
      label: t("identityInquiry.birthPlace"),
      value: response.birthPlace ? response.birthPlace : "نامشخص",
    },
    {
      label: t("identityInquiry.birthDate"),
      value: response.birthDate
        ? formatBirthDate(response.birthDate)
        : "نامشخص",
    },
    {
      label: t("identityInquiry.gender"),
      value: response.gender
        ? response.gender === "MALE"
          ? t("identityInquiry.male")
          : t("identityInquiry.female")
        : "نامشخص",
    },
    {
      label: t("identityInquiry.identificationNumber"),
      value: response.identificationNumber
        ? response.identificationNumber === 0
          ? response.nationalCode
          : response.identificationNumber.toString()
        : "نامشخص",
    },
    {
      label: t("identityInquiry.identificationSerialCode"),
      value: response.identificationSerialCode
        ? convertIdentificationNumber(response.identificationSerialCode)
        : "نامشخص",
    },
    {
      label: t("identityInquiry.identificationSerialNumber"),
      value: response.identificationSerialNumber
        ? response.identificationSerialNumber.toString()
        : "نامشخص",
    },
  ];
  const pdfContentRef = useRef<HTMLDivElement>(null);
  return (
    <div className="shadow-main from-teal-ocean relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[640px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          {t("identityInquiry.title")}
        </h2>
        <InputDataContent
          content={inputContent}
          className="le p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />
        <FlexBetween className="mb-3 items-center">
          <h2 className="text-center text-lg font-bold">
            {t("accountToIban.resultTitle")}
          </h2>
          <DownloadPdf
            id="identity-inquiry-download-pdf"
            resultContent={resultContent}
            fileName="استعلام ثبت احوال"
            tableTitle="استعلام ثبت احوال"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
        </FlexBetween>
        <ResultContent content={resultContent} />
        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <DownloadPdf
            id="identity-inquiry-download-pdf"
            resultContent={resultContent}
            fileName="استعلام ثبت احوال"
            tableTitle="استعلام ثبت احوال"
            className="order-2 flex cursor-pointer items-center justify-center rounded-md border-none! bg-red-700 px-8 py-[6px] text-white transition-all hover:bg-red-800 md:order-1 lg:rounded-lg lg:py-[8px]"
            showIcon={false}
            labelClass="text-xs lg:text-sm xl:text-base font-medium"
            useHtmlToPdf={true}
            htmlContentRef={pdfContentRef}
          />
          <div className="order-1 mb-4 flex gap-4 md:order-2 md:m-0 xl:gap-6">
            <CustomButton
              id="details-identity-inquiry-back"
              className={cn("w-fit bg-gray-500")}
              onClick={() =>
                router.push(routes.main.INQUIRY_HISTORY.primaryPath)
              }
            >
              {t("companyInformationInquiry.backButton")}
            </CustomButton>
            <CustomButton
              id="details-identity-inquiry-new-request"
              onClick={() =>
                router.push(routes.services.Identity_Inquiry.rewritePath)
              }
              className={cn("bg-primary-main mx-auto w-fit px-8! text-white")}
            >
              {t("newRequest")}
            </CustomButton>
          </div>
        </FlexCenter>
      </FlexCol>
      {/* This div will be used for PDF generation - DetailCreditCard style */}
      <div
        ref={pdfContentRef}
        style={{
          position: "fixed",
          left: "-9999px",
          top: "-9999px",
          width: "800px",
          fontFamily: "IRANYekan, sans-serif",
          margin: "2rem auto",
        }}
      >
        <PdfTemplate
          tableTitle="استعلام ثبت احوال"
          resultContent={resultContent}
          code={data.code}
          dateTime={data.created_at}
        />
      </div>
    </div>
  );
};

export default IdentityInquiryDetails;
